/**
 * P4 / R11 — purge des traces privées du portail sur l'appareil.
 *
 * Le portail est consulté par les CLIENTS de l'artisan, souvent depuis un poste
 * partagé (comptoir, tablette de boutique, ordinateur familial). À la déconnexion,
 * plus rien de nominatif ne doit rester récupérable hors ligne : ni réponse API en
 * Cache Storage, ni JWT ou identité en `localStorage`.
 *
 * Module VOLONTAIREMENT autonome (aucun import applicatif) pour être appelable
 * depuis n'importe quel point de sortie : `logout()` du hook d'auth, expiration de
 * session (401), bascule de tenant.
 *
 * Point d'appel actuel : `usePortalAuth.logout()`.
 */

/**
 * Clés exactes écrites par le portail (cf. usePortalAuth / portal-client).
 *
 * LISTE UNIQUE, exportée : `clearPortalSession()` (expiration de session) et
 * `logout()` la partagent. Deux listes recopiées avaient déjà divergé — la purge
 * sur 401 n'effaçait que `portal_token` et laissait l'identité du client, son
 * fournisseur et son drapeau mot-de-passe sur un poste souvent partagé.
 */
export const PORTAL_SESSION_KEYS = ["portal_token", "portal_client", "portal_mcp", "portal_tenant"];

/**
 * Préfixes balayés en complément : marqueurs de perf/onboarding ajoutés au fil de
 * l'eau, et les clés PostHog (`ph_<token>_posthog`) qui portent le distinct_id du
 * client — un identifiant de personne au sens RGPD, donc à effacer aussi.
 */
const KEY_PREFIXES = ["portal_", "anto_portal_", "ph_"];

function purgeStorage(store: Storage): void {
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (!key) continue;
    if (PORTAL_SESSION_KEYS.includes(key) || KEY_PREFIXES.some((p) => key.startsWith(p))) {
      doomed.push(key);
    }
  }
  // Suppression HORS de la boucle d'itération : retirer une clé pendant le parcours
  // décale les index et laisserait des rescapées.
  for (const key of doomed) store.removeItem(key);
}

/**
 * Vide TOUS les caches du Cache Storage puis les clés de session du portail.
 *
 * Le précache Workbox (shell public : html/js/css/icônes) part avec le reste : on
 * privilégie la garantie « zéro octet résiduel » à la disponibilité hors ligne. Le
 * shell est rechargé depuis le réseau à la navigation suivante — Workbox retombe
 * sur le réseau quand une entrée précachée manque — puis reconstitué à la prochaine
 * mise à jour du service worker.
 *
 * `registration.update()` plutôt qu'`unregister()` : désinscrire supprimerait
 * l'installation PWA (icône sur l'écran d'accueil) alors qu'aucune donnée privée ne
 * transite par le SW du portail (tout est NetworkOnly, cf. vite.config.ts).
 *
 * Ne throw jamais : une déconnexion doit aboutir même si le navigateur refuse
 * l'accès au Cache Storage (mode privé, stockage désactivé).
 */
export async function purgePrivateCaches(): Promise<void> {
  try {
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch {
    /* stockage indisponible — on continue, la purge du localStorage prime */
  }

  try {
    purgeStorage(window.localStorage);
    purgeStorage(window.sessionStorage);
  } catch {
    /* idem */
  }

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.update()));
    }
  } catch {
    /* le SW se remettra à jour tout seul au prochain chargement */
  }
}
